import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Acknowledged,
  Acknowledgement,
  ACKNOWLEDGEMENT_TYPE,
  Acknowledgements,
  AcknowledgementsResponse,
  AcknowledgementsResponseSchema,
  AcknowledgementType,
  AcknowledgeResponse,
  AcknowledgeResponseSchema,
} from '../../models/acknowledgements.model';
import { DocumentWithAcknowledgement } from '../../models/document.model';
import { DocumentService } from '../document/document.service';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class AcknowledgementsService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly documentService = inject(DocumentService);

  /**
   * Gets acknowledgements for a specific client from both legacy and Documents API
   * @param clientId - The client ID to get acknowledgements for
   * @param brokerageId - The brokerage ID for the Documents API header
   * @returns Observable that emits the combined list of acknowledgements
   */
  get(clientId: number, brokerageId: string): Observable<Acknowledgements> {
    // Fetch from both endpoints in parallel
    return forkJoin({
      legacy: this.getLegacyAcknowledgements(clientId).pipe(catchError(() => of([]))),
      documents: this.documentService
        .getDocumentsRequiringAcknowledgement(clientId, brokerageId)
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ legacy, documents }) => {
        // Filter out legacy documents from Documents API to prevent duplicates
        // Legacy documents are already included in the legacy endpoint response
        const nonLegacyDocuments = documents.filter((doc) => !doc.isLegacy);

        // Convert Documents API response to Acknowledgement format
        const documentAcknowledgements: Acknowledgements = nonLegacyDocuments.map((doc) =>
          this.convertDocumentToAcknowledgement(doc)
        );

        // Merge both arrays
        return [...legacy, ...documentAcknowledgements];
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Gets legacy acknowledgements for a specific client
   * @param clientId - The client ID to get acknowledgements for
   * @returns Observable that emits the list of legacy acknowledgements
   */
  private getLegacyAcknowledgements(clientId: number): Observable<Acknowledgements> {
    return this.http.get<Acknowledgements>(this.getAcknowledgementsUrl(clientId)).pipe(
      this.verifyResponse(AcknowledgementsResponseSchema),
      map((response: AcknowledgementsResponse) => {
        // Format titles for acknowledgements that don't have a title
        return response.Acknowledgements.map((ack) => ({
          ...ack,
          AcknowledgementTitle:
            ack.AcknowledgementTitle || this.formatFileNameAsTitle(ack.AcknowledgementFileName),
        }));
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Formats a filename as a title by removing extension and adding spaces between capital letters
   * @param fileName - The filename to format (e.g., "MyDocument.pdf")
   * @returns Formatted title (e.g., "My Document")
   */
  private formatFileNameAsTitle(fileName: string): string {
    if (!fileName) return '';

    // Remove file extension
    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');

    // Add spaces before capital letters (except the first character)
    const titleWithSpaces = nameWithoutExtension.replace(/([A-Z])/g, ' $1').trim();

    return titleWithSpaces;
  }

  /**
   * Converts a DocumentWithAcknowledgement to Acknowledgement format
   * @param doc - The document to convert
   * @returns Acknowledgement object
   */
  private convertDocumentToAcknowledgement(doc: DocumentWithAcknowledgement): Acknowledgement {
    return {
      AcknowledgementType: 'Document',
      AcknowledgementId: doc.legacyDocumentId || 0,
      AcknowledgementTitle: this.formatFileNameAsTitle(doc.fileName),
      AcknowledgementFileName: doc.fileName,
      ContentType: doc.contentType,
      ClientId: doc.clientIds[0] || 0,
      DocumentId: doc.documentId && doc.documentId.length > 0 ? doc.documentId : undefined,
      IsLegacy: doc.isLegacy,
    };
  }

  /**
   * Downloads an acknowledgement file for a specific client
   * @param clientId - The client ID
   * @param acknowledgementId - The acknowledgement ID to download
   * @param acknowledgementType - The acknowledgement type (Document or Acknowledgement)
   * @param documentId - Optional UUID for Documents API downloads
   * @param isLegacy - Flag indicating if this is a legacy document
   * @param brokerageId - Optional brokerage ID for Documents API
   * @returns Observable that emits the file blob
   */
  download(
    clientId: number,
    acknowledgementId: number,
    acknowledgementType: string,
    documentId?: string,
    isLegacy?: boolean,
    brokerageId?: string
  ): Observable<Blob> {
    // If this is NOT legacy and we have a documentId (UUID), use the Documents API download
    if (!isLegacy && documentId && brokerageId) {
      return this.documentService.downloadDocumentByUuid(documentId, brokerageId);
    }

    // Otherwise, use legacy endpoints
    const url =
      acknowledgementType === 'Document'
        ? this.getDocumentsUrl(clientId, acknowledgementId)
        : `${this.getAcknowledgementsUrl(clientId)}/${acknowledgementId}`;

    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Acknowledges a list of acknowledgements for a specific client
   * @param clientId - The client ID to acknowledge acknowledgements for
   * @param brokerageId - The brokerage ID for Documents API
   * @param acknowledgements - Array of acknowledgements to acknowledge
   * @returns Observable that emits all acknowledgement results
   */
  acknowledge(
    clientId: number,
    brokerageId: string,
    acknowledgements: {
      acknowledgementId: number;
      acknowledgementType: AcknowledgementType;
      documentId?: string;
      isLegacy?: boolean;
    }[]
  ): Observable<AcknowledgeResponse[]> {
    const acknowledgementRequests: Observable<AcknowledgeResponse[]> = forkJoin(
      acknowledgements.map((ack) => {
        if (!ack.isLegacy && ack.documentId) {
          return this.documentService
            .acknowledgeDocument(ack.documentId, clientId, brokerageId)
            .pipe(
              map(
                (response): AcknowledgeResponse => ({
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  Acknowledged: response.acknowledged,
                  success: response.acknowledged,
                })
              ),
              catchError((error: HttpErrorResponse): Observable<AcknowledgeResponse> => {
                const result: AcknowledgeResponse = {
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  success: false,
                  error: error.message || 'Unknown error occurred',
                };
                return of(result);
              })
            );
        }

        // Otherwise, use legacy endpoint
        const acknowledgeRequest: Acknowledged = {
          ClientId: clientId,
          AcknowledgementId: ack.acknowledgementId,
          AcknowledgementType: ACKNOWLEDGEMENT_TYPE[ack.acknowledgementType],
          Acknowledged: true,
        };

        return this.http
          .put<AcknowledgeResponse>(this.getAcknowledgementsUrl(clientId), acknowledgeRequest)
          .pipe(
            this.verifyResponse(AcknowledgeResponseSchema),
            map(
              (response: AcknowledgeResponse): AcknowledgeResponse => ({
                ClientId: response.ClientId,
                AcknowledgementType: response.AcknowledgementType,
                AcknowledgementId: response.AcknowledgementId,
                Acknowledged: response.Acknowledged,
                DocumentId: response.DocumentId,
                success: response.Acknowledged ?? true,
                error: response.error,
                message: response.message,
              })
            ),
            catchError((error: HttpErrorResponse): Observable<AcknowledgeResponse> => {
              const result: AcknowledgeResponse = {
                AcknowledgementId: ack.acknowledgementId,
                AcknowledgementType: ack.acknowledgementType,
                success: false,
                error: error.message || 'Unknown error occurred',
              };
              return of(result);
            })
          );
      })
    );

    return acknowledgementRequests.pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  private getAcknowledgementsUrl(clientId: number): string {
    return environment.acknowledgements.replace('{{clientId}}', clientId.toString());
  }

  private getDocumentsUrl(clientId: number, acknowledgementId: number): string {
    return environment.documents
      .replace(/\{\{clientId\}\}/g, clientId.toString())
      .replace(/\{\{documentId\}\}/g, acknowledgementId.toString());
  }
}
